<template>

  <div class="map-wrapper">

    <gmap-map
      ref="mapRef"
      :center="mapCenter"
      :zoom="mapZoom"
      @zoom_changed="onZoomChanged($event)"
      @center_changed="onCenterChanged($event)"
      class="gmap-map"
      :options="mapOptions"
    >

      <!-- User location -->
      <gmap-marker
        :position="userLocation"
        :icon="userLocationIcon"
        :z-index="99999999"
      ></gmap-marker>

      <!-- Challenge markers -->
      <gmap-marker
        v-for="(challenge, index) in markers"
        :key="index"
        :position="challenge.location"
        :clickable="true"
        @click="onMarkerClicked(challenge)"
        :icon="getIconForChallenge(challenge)"
      ></gmap-marker>

      <!-- Challenge small area circles, useful to know if user is inside -->
      <gmap-circle
        v-for="(challenge, index) in markers"
        :key=`${index}_`
        :center="challenge.location"
        :radius="checkpointCircleRadius"
        :options="circleOptions"
      ></gmap-circle>

      <v-card slot="visible" v-if="clickedChallenge" class="marker-info-card slide-from-down">
        <v-card-title primary-title>
          <div class="card-content-wrapper">
            <div class="headline-wrapper">
              <h3 class="headline">
                {{ clickedChallenge.name }}
              </h3>
              <a href="#" @click.prevent="clickedChallenge = null">
                <v-icon>
                  close
                </v-icon>
              </a>
            </div>
            <div class="sub-info">
              <div>
                Status: {{ clickedChallenge.status }}
              </div>
              <div>
                Worth: {{ clickedChallenge.points }} points + bonus
              </div>
              <div>
                Activity type: {{ clickedChallenge.activityType }}
              </div>
            </div>
          </div>
        </v-card-title>
        <v-card-actions v-if="showGoToQuestionButton(clickedChallenge)">
          <v-spacer></v-spacer>
          <v-btn primary @click.native="goToQuestion(clickedChallenge.challengeId)" class="mr-2">
            Go to question
          </v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>

      <div slot="visible">
        <div class="heart-wrapper">
          <heart-component></heart-component>
        </div>
      </div>

    </gmap-map>

  </div>

</template>

<script>
  /* eslint-disable no-undef */

  import {
    debounce,
    filter,
    forEach,
    get,
    size,
  } from 'lodash'
  import Vue from 'vue'
  import { mapActions, mapGetters } from 'vuex'
  import HeartComponent from '@/components/single/Heart'
  import { challengeStatus, challengeActivityType } from '@/services/challengeStatus.enum'
  import { difficultyMap } from '@/services/difficulty.enum'

  const getters = mapGetters([
    'userLocation',
    'challengesLocations',
    'drawerState',
    'mapZoomState',
    'mapCenterState',
    'game',
    'questionRadius'
  ])

  const actions = mapActions([
    'getChallengesLocations',
  ])

  // Center of the map at start (will be changed by defineCenterAndZoom())
  const sydney2000 = {
    lat: -33.867888,
    lng: 151.2012157,
  }

  const zoomStartShowCircles = 15
  // https://developers.google.com/maps/documentation/javascript/examples/circle-simple
  const circleOptions = {
    fillColor: 'transparent',
    strokeWeight: 0,
  }
  const circleOptionsIfZoom = {
    fillColor: 'white',
    fillOpacity: 0.6,
    strokeWeight: 0,
  }

  export default {
    name: 'map',
    components: {
      HeartComponent,
    },
    created() {
      if (this.mapZoomState == null) {
        this.mapZoom = this.game.gameZoom
      }
    },
    data () {
      return {
        mapZoom: null,

        markers: [],

        // In meters
        checkpointCircleRadius: 80,

        userLocationIcon: {
          url: '/static/img/map/here-google-like.svg',
          // Size of the original image
          scaledSize: new google.maps.Size(30, 30),
          // The origin for this image is (0, 0)
          origin: new google.maps.Point(0, 0),
          // The anchor for this image is the middle of the base
          anchor: new google.maps.Point(15, 15),
        },

        clickedChallenge: null,

        circleOptions: {
          fillColor: 'transparent',
          strokeWeight: 0,
        },

        // https://developers.google.com/maps/documentation/javascript/examples/control-positioning
        mapOptions: {
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: false,
          clickableIcons: false,
        },

        mapObject: null,
      }
    },
    mounted() {
      this.checkpointCircleRadius = this.questionRadius
      this.getChallengesLocations()
        .then(() => {
          if (this.challengesLocations) {
            this.markers = this.filterChallengesWithLocations(this.challengesLocations)

            if (size(this.challengesLocations) > 0 && !this.mapZoomState && !this.mapCenterState) {
              this.defineCenterAndZoom()
            }
          }
        })

      setTimeout(() => {

        this.mapObject = this.$refs.mapRef.$mapObject

        // Add "Locate Me" custom control to center map on user location
        const centerControlDiv = this.buildCenterControlDiv()
        centerControlDiv.index = 1
        this.mapObject.controls[google.maps.ControlPosition.RIGHT_BOTTOM].push(centerControlDiv)

        this.showCirclesAroundCheckpoints(this.mapZoom)

        // Set back previous 'mapZoom' and 'mapCenter' if any
        if (this.mapZoomState && this.mapCenterState) {
          this.mapZoom = this.mapZoomState
        } else if (size(this.markers) > 0) {
          // In order to show all markers
          this.defineCenterAndZoom()
        }
      })
    },
    computed: {
      ...getters,
      mapCenter: function () {
        return this.mapCenterState ? this.mapCenterState : sydney2000
      }
    },
    watch: {
      userLocation: function () {
        if (this.challengesLocations && this.challengesLocations.length > 0) {
          this.checkIfUserInZone()
        }
      },
      challengesLocations: function (locations) {
        this.markers = this.filterChallengesWithLocations(locations)

        if (size(this.challengesLocations) > 0 && !this.mapZoomState && !this.mapCenterState) {
          this.defineCenterAndZoom()
        }

        if (this.userLocation) {
          this.checkIfUserInZone()
        }
      },
      drawerState: function () {
        // Need to wait until end of drawer hide animation!
        setTimeout(() => {
          this.onResizeMap()
        }, 50)
      },
    },
    methods: {
      ...actions,

      filterChallengesWithLocations(questions) {
        return filter(questions, q => !!q.location)
      },

      onResizeMap() {
        Vue.$gmapDefaultResizeBus.$emit('resize')
      },

      checkIfUserInZone() {
        let isUserInOneZone = false
        let challengeId = null

        const sphericalMaps = get(google, 'maps.geometry.spherical')
        if (sphericalMaps) {

          forEach(this.markers, marker => {

            const markerLat = marker.location.lat
            const markerLng = marker.location.lng

            const markerPositionPoint = new google.maps.LatLng(markerLat, markerLng)
            const userLocationPoint = new google.maps.LatLng(this.userLocation.lat, this.userLocation.lng)

            const distanceToMarker = sphericalMaps.computeDistanceBetween(markerPositionPoint, userLocationPoint)
            isUserInOneZone = distanceToMarker <= this.checkpointCircleRadius
            challengeId = marker.challengeId

            // No need to check other checkpoints
            // (Maybe yes, if overlapping between zones?...)
            if (isUserInOneZone) {
              return false
            }
          })

          this.$store.commit('SET_IS_USER_AT_CHALLENGE_LOCATION', {
            isUserInOneZone,
            challengeId,
          })
        }
      },

      showGoToQuestionButton(clickedChallenge) {
        return clickedChallenge.status === challengeStatus.UNLOCKED ||
          clickedChallenge.status === challengeStatus.SOLVED
      },

      onMarkerClicked(challenge) {
        this.clickedChallenge = challenge
      },

      goToQuestion(challengeId) {
        this.$router.push({ name: 'Question', params: { id: challengeId } })
      },

      getIconForChallenge(challenge) {

        let iconImg = challenge.activityType === challengeActivityType.FINISH_LINE ? 'checkered-flag-' : ''

        if (challenge.status === challengeStatus.SOLVED) {
          iconImg += 'challenge-correct.png'
        } else if (challenge.status === challengeStatus.UNLOCKED) {
          iconImg += 'challenge-unlocked.png'
        } else if (challenge.status === challengeStatus.FAILED) {
          iconImg += 'challenge-failed.png'
        } else {
          iconImg += 'challenge-locked.png'
        }
        return this.getIcon(iconImg)
      },

      getIcon(iconImg) {

        // If no icon defined here, default red marker from Google is used

        let icon

        // Working PNG!
        // https://developers.google.com/maps/documentation/javascript/examples/icon-complex
        const width = 120
        const height = 166
        const scaledWidth = width / 4
        const scaledHeight = height / 4
        icon = {
          url: `/static/img/map/${iconImg}`,
          // Size of the original image
          scaledSize: new google.maps.Size(scaledWidth, scaledHeight),
          // The origin for this image is (0, 0)
          origin: new google.maps.Point(0, 0),
          // The anchor for this image is the middle of the base
          anchor: new google.maps.Point(scaledWidth / 2, scaledHeight),
        }

        // Better to use a SVG, but harder... :(
        /**
         * In computed / markers:
         * return this.challengesLocations.slice(0, 4) ==> Works
         * return this.challengesLocations.slice(0, 5) ==> Breaks...
         */
        // https://codepen.io/defvayne23/pen/EVYGRw
        // https://github.com/defvayne23/SVGMarker/blob/master/SVGMarker.js

        return icon
      },

      /**
       * Show or hide checkpoint circles depending on the map zoom
       */
      onZoomChanged(newZoom) {
        this.showCirclesAroundCheckpoints(newZoom)

        // Save 'zoom' to state --> get it back when coming back from another screen
        this.$store.commit('SET_MAP_ZOOM', newZoom)
      },
      showCirclesAroundCheckpoints(zoom) {
        if (zoom >= zoomStartShowCircles) {
          this.circleOptions = circleOptionsIfZoom
        } else {
          this.circleOptions = circleOptions
        }
      },

      /**
       * Update map center in Vue component
       * (Otherwise "locate-me' icon works only once)
       */
      onCenterChanged: debounce(function (newCenter) {
        this.$store.commit('SET_MAP_CENTER', newCenter)
      }, 150),

      /**
       * Add "Locate Me" custom control to center map on user location
       */
      // https://developers.google.com/maps/documentation/javascript/examples/control-custom
      buildCenterControlDiv() {

        const controlDiv = document.createElement('div')

        // Set CSS for the control border.
        const controlUI = document.createElement('div')
        controlUI.style.backgroundColor = '#fff'
        controlUI.style.border = '2px solid #fff'
        controlUI.style.borderRadius = '2px'
        controlUI.style.boxShadow = '0 1px 2px rgba(0,0,0,.2)'
        controlUI.style.cursor = 'pointer'
        controlUI.style.marginRight = '10px'
        controlUI.style.textAlign = 'center'
        controlDiv.appendChild(controlUI)

        // Set CSS for the control interior.
        const controlText = document.createElement('div')
        controlText.style.width = '24px'
        controlText.style.fontFamily = 'Roboto,Arial,sans-serif'
        controlText.style.fontSize = '16px'
        controlText.style.paddingTop = '5px'
        controlText.innerHTML = '<img src="/static/img/map/locate-me.png" style="width: 15px;" />'
        controlUI.appendChild(controlText)

        controlUI.addEventListener('click', () => {
          this.$store.commit('SET_MAP_CENTER', this.userLocation)
        })

        return controlDiv
      },

      defineCenterAndZoom() {
        console.log(`Setting center to ${JSON.stringify({lat: this.game.gameLatitude, lng: this.game.gameLongitude})}`)
        this.$store.commit('SET_MAP_CENTER', {lat: this.game.gameLatitude, lng: this.game.gameLongitude})

      },
      getDifficulty(difficulty) {
        return difficultyMap[difficulty]
      }

    },
  }
</script>

<style lang="scss" type="text/scss" scoped>

  .map-wrapper {
    height: 100%;
    flex: 1 1 auto;
    display: flex;

    .gmap-map {
      width: 100vw;
      flex: 1 1 auto;
    }
  }

  .marker-info-card {
    position: absolute;
    bottom: 0;
    right: 0;
    width: 96%;
    max-width: 500px;
    margin: 2%;
    z-index: 1;

    .card-content-wrapper {
      width: 100%;

      .headline-wrapper {
        display: flex;

        h3 {
          flex: 1 1 auto;
          text-align: left;
        }

        a {
          align-items: baseline;
          margin-top: -12px;
          margin-right: -5px;
          text-decoration: none;
        }
      }

      .sub-info {
        display: flex;

        > div {
          flex: 1 1 auto;
          text-align: left;
        }
      }
    }

    .card__actions {
      margin: 0 0 8px 8px;
    }
  }

  @media screen and (min-width: 520px) {
    .marker-info-card {
      right: calc(48% - 250px);
    }
  }

  .heart-wrapper {
    position: absolute;
    bottom: 0;
    left: calc(50% - 130px);
    text-align: center;
    margin-bottom: 5px;
  }

  .slide-from-down {
    animation: slideFromDown 0.4s forwards;
    overflow: hidden;
  }

  @keyframes slideFromDown {
    0% {
      transform: translateY(100px);
      opacity: 0.5;
    }
    100% {
      transform: translateY(0);
      opacity: 1;
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/single/Map.vue